import { useProfessionalSchedule } from '@/modules/Telemedicine/queries/ScheduleAppointment/Telemedicine.queries'
import {
  EventActionsStep,
  EventOnConfirm,
  useMediaQuery,
} from '@conexasaude/conexa-hero'
import { addDays, format, parse, subDays } from 'date-fns'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { ProfessionalCardProps } from './ProfessionalBookingCard.types'
import { currentDateFormatted, endDateFormatted } from './constants'
import { autoCapitalize } from '@conexasaude/front-utils'
import useScheduleAppointmentStore from '@/modules/Telemedicine/store/ScheduleAppointment/ScheduleAppointment'
import { useModalStore } from '@/store/useModal'
import { Profile } from '@/modules/Telemedicine/services/mapper/ProfessionalsMapper'

export const useProfessionalBookingCard = ({
  professional,
  startDateSlot,
}: ProfessionalCardProps) => {
  const isMobile = useMediaQuery('(max-width: 768px)')

  const [startDate, setStartDate] = useState<string>(null)
  const [endDate, setEndDate] = useState<string>(null)
  const [selectedDateSlot, setSelectedDateSlot] = useState<string>(null)
  const [selectedTimeSlot, setSelectedTimeSlot] = useState<string>(null)

  const { modalOpen, showModal, closeModal } = useModalStore()

  const {
    patient,
    clinic,
    objectiveAppointment,
    healthInsuranceData,
    dataFormatted,
  } = useScheduleAppointmentStore()

  const { data, isLoading } = useProfessionalSchedule({
    professionalId: professional?.id,
    clinicId: clinic?.id,
    startDate,
    endDate,
    patientId: patient?.patientId,
    professionalType: dataFormatted?.professionalTypeKey,
    ...(professional?.councilType === 'CRP' && {
      isPsychologistAlreadyMigrated: true,
    }),
  })

  const renderProfessionalDetails = useCallback(
    (professional: Profile['professional']) => {
      return {
        about: professional?.description,
        council: `${professional?.councilType}: ${professional?.councilNumer} (${professional?.councilUf})`,
        id: professional?.id,
        name: autoCapitalize(professional?.name),
        photo: professional?.urlPhoto,
        specialty: professional?.specialtyName,
        duration: professional?.appointmentDuration,
      }
    },
    [],
  )

  const handleNextDateChange = (event: EventActionsStep) => {
    const nextStartDate = addDays(new Date(event?.finalDate), 1)
    const nextStartDateFormatted = format(nextStartDate, 'yyyy-MM-dd')

    const nextEndDate = addDays(nextStartDate, isMobile ? 3 : 6)
    const nextEndDateFormatted = format(nextEndDate, 'yyyy-MM-dd')

    setStartDate(nextStartDateFormatted)
    setEndDate(nextEndDateFormatted)
  }

  const handlePreviousDateChange = async (event: EventActionsStep) => {
    const previousInitialDate = subDays(
      new Date(event?.initialDate),
      isMobile ? 4 : 7,
    )
    const previousDateFormatted = format(previousInitialDate, 'yyyy-MM-dd')

    const previousEndDate = addDays(previousInitialDate, isMobile ? 3 : 6)
    const previousEndDateFormatted = format(previousEndDate, 'yyyy-MM-dd')

    setStartDate(previousDateFormatted)
    setEndDate(previousEndDateFormatted)
  }

  const handleConfirmShchedule = useCallback(
    (event: EventOnConfirm) => {
      const startDateFormatted = format(event?.date, 'dd/MM/yyyy')

      setSelectedDateSlot(startDateFormatted)
      setSelectedTimeSlot(event?.slot?.time)

      showModal('SCHEDULE_APPOINTMENT')
    },
    [showModal],
  )

  const handleCloseModal = useCallback(() => {
    setSelectedDateSlot(null)
    setSelectedTimeSlot(null)
    closeModal()
  }, [closeModal])

  const contentObject = useMemo(() => {
    return {
      patient: {
        patientId: patient?.patientId,
        patientName: patient?.patientName,
        patientEmail: patient?.patientEmail,
        patientPhone: patient?.patientPhone,
      },
      professional: {
        professionalId: professional?.id,
        professionalName: professional?.name,
      },
      appointmentObject: {
        value: objectiveAppointment?.value,
        key: objectiveAppointment?.key,
      },
      fitIn: Boolean(dataFormatted?.noScheduleAvailable),
      appointmentDate: selectedDateSlot,
      appointmentHour: selectedTimeSlot,
      professionalName: professional?.name,
      professionalClinic: clinic?.name,
      professionalSpecialty: professional?.specialtyName,
      councilType: professional?.councilType,
      councilNumber: professional?.councilNumer,
      professionalPhotoUrl: professional?.urlPhoto,
      healthInsuranceData,
      multipleHealthInsuranceCards:
        healthInsuranceData?.multipleHealthInsuranceCards,
    }
  }, [
    patient?.patientId,
    patient?.patientName,
    patient?.patientEmail,
    patient?.patientPhone,
    professional?.id,
    professional?.name,
    professional?.specialtyName,
    professional?.councilType,
    professional?.councilNumer,
    professional?.urlPhoto,
    objectiveAppointment?.value,
    objectiveAppointment?.key,
    dataFormatted?.noScheduleAvailable,
    clinic?.name,
    healthInsuranceData,
    selectedDateSlot,
    selectedTimeSlot,
  ])

  useEffect(() => {
    if (startDateSlot) {
      const parsedStartDate = parse(startDateSlot, 'yyyy-MM-dd', new Date())

      const professionalStartDate = format(parsedStartDate, 'yyyy-MM-dd')
      const professionalEndDate = format(
        addDays(parsedStartDate, 6),
        'yyyy-MM-dd',
      )

      setStartDate(professionalStartDate)
      setEndDate(professionalEndDate)

      return
    }

    setStartDate(currentDateFormatted)
    setEndDate(endDateFormatted)
  }, [startDateSlot])

  return {
    data,
    isLoading,
    modalOpen,
    contentObject,
    selectedDateSlot,
    handleCloseModal,
    handleConfirmShchedule,
    renderProfessionalDetails,
    handleNextDateChange,
    handlePreviousDateChange,
  }
}
